import { useEffect, useState } from 'react'
import type SwiperCore from 'swiper'
import { A11y, Zoom } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { type SwiperOptions } from 'swiper/types'
import { cn } from '#app/shared/lib/misc.js'
import 'swiper/swiper-bundle.css'

type Props = {
  slides: any[]
  initialIndex?: number
  slidesPerView: number
  paginationContainerClassName?: string
  className?: string
  withPagination?: boolean
  spaceBetween?: number
  loop?: boolean
  breakpoints?: SwiperOptions['breakpoints']
  swiper?: SwiperCore
  setSwiper?: (swiper: SwiperCore) => void
  activeIndex?: number
  onActiveIndexChange?: (index: number) => void
  zoom?: boolean
  onZoomChange?: (isZoomed: boolean) => void
  swiperOptions?: Partial<SwiperOptions> // Add this line
}

export const Carousel = ({
  initialIndex = 0,
  loop,
  slides,
  spaceBetween,
  slidesPerView = 1,
  paginationContainerClassName,
  withPagination,
  className,
  breakpoints,
  swiper: externalSwiper,
  setSwiper,
  activeIndex: controlledActiveIndex,
  onActiveIndexChange,
  zoom = false,
  swiperOptions = {}, // Add this line
}: Props) => {
  const [internalSwiper, setInternalSwiper] = useState<SwiperCore | undefined>(
    undefined,
  )
  const [internalActiveIndex, setInternalActiveIndex] = useState(initialIndex)

  const activeIndex =
    controlledActiveIndex !== undefined
      ? controlledActiveIndex
      : internalActiveIndex

  const swiper = externalSwiper || internalSwiper

  useEffect(() => {
    if (externalSwiper) {
      setInternalActiveIndex(externalSwiper.realIndex)
    }
  }, [externalSwiper])

  const handleActiveIndexChange = (index: number) => {
    setInternalActiveIndex(index)
    onActiveIndexChange?.(index)
  }

  const handlePaginationClick = (index: number) => {
    if (loop) {
      swiper?.slideToLoop(index)
    } else {
      swiper?.slideTo(index)
    }
    handleActiveIndexChange(index)
  }

  return (
    <Swiper
      {...swiperOptions} // Add this line
      zoom={zoom}
      initialSlide={initialIndex}
      spaceBetween={spaceBetween}
      className={className}
      modules={[A11y, Zoom]}
      breakpoints={breakpoints}
      slidesPerView={slidesPerView}
      onSwiper={(newSwiper) => {
        if (setSwiper) {
          setSwiper(newSwiper)
        } else {
          setInternalSwiper(newSwiper)
        }
      }}
      loop={loop}
      onSlideChange={(changedSwiper) => {
        handleActiveIndexChange(changedSwiper.realIndex)
      }}
    >
      {slides.map((slide, index) => (
        <SwiperSlide zoom={zoom} key={index}>
          {slide}
        </SwiperSlide>
      ))}
      {withPagination && (
        <div
          slot="container-end"
          className={cn(
            'mt-9 flex justify-center gap-2',
            paginationContainerClassName,
          )}
        >
          {slides.map((_, index) => (
            <button
              key={index}
              className="flex h-[24px] items-center"
              onClick={() => handlePaginationClick(index)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handlePaginationClick(index)
                }
              }}
            >
              <div
                className={`
                  h-[4px] w-9 min-w-9 rounded-[4px]

                  ${activeIndex === index ? 'bg-pink-600' : 'bg-purple-300'}
                `}
              />
            </button>
          ))}
        </div>
      )}
    </Swiper>
  )
}
